import { useState } from 'react'
import { MiniTopic } from '@deepguide-ai/types'
import { Button } from '@deepguide-ai/ui/button'
import { Separator } from '@deepguide-ai/ui/separator'
import { ToolInvocationUIPart } from 'ai'
import { ChevronUp, ListPlus, MessageSquareText, Shuffle } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { cn } from '@/lib/utils'
import NoResults from './NoResults'

const TopicSuggestionMessage = ({
  part,
  icon,
}: {
  part: ToolInvocationUIPart
  icon: React.ReactNode
}) => {
  const [showDetails, setShowDetails] = useState(false)

  /* FIXME(@ai-sdk-upgrade-v5): The `part.toolInvocation.state` property has been removed. Please manually migrate following https://ai-sdk.dev/docs/migration-guides/migration-guide-5-0#tool-part-type-changes-uimessage */
  const topicSuggestion: MiniTopic | null =
    part.toolInvocation.state === 'result'
      ? part.toolInvocation.result.topic
      : null

  /* FIXME(@ai-sdk-upgrade-v5): The `part.toolInvocation.state` property has been removed. Please manually migrate following https://ai-sdk.dev/docs/migration-guides/migration-guide-5-0#tool-part-type-changes-uimessage */
  const action: string | null =
    part.toolInvocation.state === 'result'
      ? part.toolInvocation.result.action
      : null
  /* FIXME(@ai-sdk-upgrade-v5): The `part.toolInvocation.state` property has been removed. Please manually migrate following https://ai-sdk.dev/docs/migration-guides/migration-guide-5-0#tool-part-type-changes-uimessage */
  const error: Error | null =
    part.toolInvocation.state === 'result'
      ? part.toolInvocation.result.error
      : null

  // console.log('>>> topicSuggestion', part, topicSuggestion)
  /* FIXME(@ai-sdk-upgrade-v5): The `part.toolInvocation.state` property has been removed. Please manually migrate following https://ai-sdk.dev/docs/migration-guides/migration-guide-5-0#tool-part-type-changes-uimessage */
  return (
    <div className='flex w-full flex-col items-start gap-0'>
      <Button
        variant='ghost'
        className='group/icon inline-flex h-auto w-full min-w-16 items-center justify-start gap-2 bg-transparent px-3 py-2.5 hover:bg-transparent'
        onClick={() => setShowDetails(!showDetails)}
      >
        <div className='text-muted-foreground flex w-4 shrink-0 items-center'>
          <div>
            {part.toolInvocation.state === 'result' ? (
              action === 'assign_existing' ? (
                <Shuffle
                  className='h-[14px] w-[14px] [&_svg]:size-[14px]'
                  strokeWidth={2}
                />
              ) : action === 'create_new' ? (
                <ListPlus
                  className='h-[14px] w-[14px] [&_svg]:size-[14px]'
                  strokeWidth={2}
                />
              ) : (
                icon
              )
            ) : (
              <MessageSquareText
                className='h-[14px] w-[14px] [&_svg]:size-[14px]'
                strokeWidth={2}
              />
            )}
          </div>
        </div>

        <span
          className={cn(
            'text-foreground flex-1 text-left text-[14px] leading-5 font-medium tracking-[0] break-words whitespace-normal'
          )}
        >
          {error ? (
            <span className='text-destructive'>
              Topic generation failed: {error.message}
            </span>
          ) : (
            <>
              {part.toolInvocation.state === 'result'
                ? action === 'assign_existing'
                  ? 'Assigned existing topic'
                  : action === 'create_new'
                    ? 'Created new topic'
                    : ''
                : 'Suggesting topic'}
            </>
          )}
        </span>
        <ChevronUp
          className={cn('size-4 transition-transform duration-300', {
            'rotate-180': !showDetails,
          })}
          strokeWidth={2}
        />
      </Button>
      {showDetails && <Separator className='my-0' />}
      <AnimatePresence>
        {showDetails && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.2, ease: [0.23, 1, 0.32, 1] },
              opacity: { duration: 0.15 },
            }}
            className='w-full overflow-hidden'
          >
            <div className='text-muted-foreground flex w-full flex-col items-start gap-2.5 px-4 py-3 text-sm'>
              {topicSuggestion ? (
                <div>
                  <h3 className='text-foreground font-semibold'>
                    {topicSuggestion.title}
                  </h3>
                  <p>{topicSuggestion.description}</p>
                </div>
              ) : (
                <NoResults />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default TopicSuggestionMessage
